// Wyoming Orders list. PDF upload + CSV import surface here. Both
// paths funnel into the same wy_ownership table via the shared
// wy-party-match resolver — operator picks the format that matches
// what they have.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { wyApi, type WyOrderListItem } from '../services/wyApi';

export function WyOrdersList() {
  const [orders, setOrders] = useState<WyOrderListItem[]>([]);
  const [err, setErr]       = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState<'pdf' | 'csv' | null>(null);

  async function reload() {
    try { const r = await wyApi.listOrders(); setOrders(r.orders); setErr(null); }
    catch (e: any) { setErr(e?.message ?? 'failed to load'); }
  }
  useEffect(() => { reload(); }, []);

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto">
      <header className="flex items-end justify-between pb-6 border-b border-border gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">workspace · wyoming</div>
          <h1 className="font-display text-[28px] leading-tight">Orders</h1>
          <div className="text-[12px] text-text-dim mt-1.5 font-mono">{orders.length} on file</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setUploadOpen('csv')}
            className="px-3 py-2 text-[12px] border border-border text-text-dim rounded hover:text-text hover:bg-bg-hover">
            ⤓ Import CSV
          </button>
          <button onClick={() => setUploadOpen('pdf')}
            className="px-3 py-2 text-[12px] bg-accent text-bg font-medium rounded hover:bg-accent-bright">
            + Upload PDF
          </button>
        </div>
      </header>

      {err && <div className="mt-4 text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>}

      <div className="mt-6">
        {orders.length === 0 && !err && (
          <div className="bg-bg-card-soft border border-border rounded px-6 py-12 text-center">
            <div className="text-[13px] text-text-dim">No Wyoming orders yet.</div>
            <div className="text-[11px] text-text-faint font-mono mt-2">
              Upload a combined PDF (page 1 + interest-schedule pages) or import a CSV of already-parsed rows.
            </div>
          </div>
        )}
        {orders.map(o => (
          <Link key={o.id} to={`/mi/wy/orders/${o.id}`}
            className="block bg-bg-card border border-border rounded p-4 mb-2 hover:border-accent/40 hover:bg-bg-card-soft">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[14px] text-text">
                  <span className="text-text-dim">WOGCC</span> <em className="text-accent not-italic">{o.order_number}</em>
                  {o.operator && <span className="text-text-dim text-[12px] ml-3">{o.operator}</span>}
                </div>
                <div className="text-[10px] font-mono text-text-faint mt-1">
                  {o.county ?? 'no county'}
                  {o.filed_date && <span> · filed {o.filed_date}</span>}
                  {o.hearing_date && <span> · hearing {o.hearing_date}</span>}
                  <span> · {o.ownership_count} rows</span>
                  {o.wi_count > 0 && <span> · {o.wi_count} WI</span>}
                </div>
              </div>
              <span className={
                'shrink-0 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded border ' +
                (o.parse_status === 'complete' ? 'border-green/40 bg-green/10 text-green'
                 : o.parse_status === 'failed' ? 'border-red/40 bg-red/10 text-red'
                 : 'border-amber/40 bg-amber/10 text-amber')
              }>{o.parse_status}</span>
            </div>
          </Link>
        ))}
      </div>

      {uploadOpen && (
        <WyOrderUploadModal kind={uploadOpen} onClose={() => setUploadOpen(null)} onDone={async () => { setUploadOpen(null); await reload(); }} />
      )}
    </div>
  );
}

// Inline upload modal — keeps the WY orders surface self-contained
// rather than splitting another file.
function WyOrderUploadModal(props: { kind: 'pdf' | 'csv'; onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [csvText, setCsvText] = useState<string>('');
  const [orderNumberOverride, setOrderNumberOverride] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  async function go() {
    setBusy(true); setErr(null); setResult(null);
    try {
      if (props.kind === 'pdf') {
        if (!file) { setErr('select a PDF'); setBusy(false); return; }
        const form = new FormData();
        form.set('pdf', file);
        if (orderNumberOverride.trim()) form.set('order_number_override', orderNumberOverride.trim());
        const r = await wyApi.uploadOrder(form);
        setResult(
          `Order ${r.order_number} · ${r.parse_status} · ${r.ownership_inserted} ownership rows inserted.` +
          (r.parse_warnings.length ? `\n\n${r.parse_warnings.length} warnings:\n` + r.parse_warnings.join('\n') : '')
        );
      } else {
        const text = file ? await file.text() : csvText;
        if (!text.trim()) { setErr('paste CSV text or pick a file'); setBusy(false); return; }
        const r = await wyApi.uploadCsv(text);
        const errLines = r.errors.length
          ? `\n\n${r.errors.length} row errors:\n` + r.errors.slice(0, 20).map(e => `  row ${e.row_number}: ${e.message}`).join('\n')
          : '';
        setResult(
          `${r.inserted_ownership} ownership rows inserted · ${r.parties_created} new parties · ${r.parties_reused} reused.` + errLines
        );
      }
      // Caller refreshes list when modal closes.
    } catch (e: any) {
      setErr(e?.message ?? 'upload failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={props.onClose}>
      <div className="bg-bg-card border border-border rounded-lg w-[680px] max-w-[94vw] max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-border flex items-center justify-between shrink-0">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">wyoming · {props.kind === 'pdf' ? 'upload pdf' : 'import csv'}</div>
            <div className="font-display text-[22px] leading-tight">
              {props.kind === 'pdf' ? 'Upload combined PDF' : 'Import CSV of ownership rows'}
            </div>
          </div>
          <button onClick={props.onClose} className="text-text-dim hover:text-text text-2xl leading-none">×</button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
          {props.kind === 'pdf' && (
            <>
              <div className="text-[12px] text-text-dim">
                Combined PDF: print page 1 (metadata + legal description) with the
                interest-schedule pages into one file. Page roles are auto-detected.
              </div>
              <input type="file" accept="application/pdf" onChange={e => setFile(e.target.files?.[0] ?? null)}
                className="block text-[12px] text-text-dim" />
              <input value={orderNumberOverride} onChange={e => setOrderNumberOverride(e.target.value)}
                placeholder="order_number override (optional — used if metadata Vision can't read it)"
                className="w-full bg-bg-input border border-border rounded px-3 py-2 text-[12px] font-mono outline-none focus:border-accent" />
            </>
          )}
          {props.kind === 'csv' && (
            <>
              <div className="text-[12px] text-text-dim">
                Required columns: order_number, party_name, township_num, township_dir, range_num,
                range_dir, section, record_class. Optional: city, state, street, zip, tract_description,
                interest_type, decimal_interest, interest_description_raw.
              </div>
              <input type="file" accept=".csv,text/csv" onChange={e => setFile(e.target.files?.[0] ?? null)}
                className="block text-[12px] text-text-dim" />
              <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mt-3">or paste directly</div>
              <textarea value={csvText} onChange={e => setCsvText(e.target.value)} rows={8}
                placeholder="order_number,party_name,township_num,..."
                className="w-full bg-bg-input border border-border rounded px-3 py-2 text-[11px] font-mono outline-none focus:border-accent" />
            </>
          )}
          {err && <div className="text-[12px] text-red bg-red/10 border border-red/40 rounded px-3 py-2">{err}</div>}
          {result && <pre className="text-[11px] text-text-dim bg-bg-input/40 border border-border rounded px-3 py-2 whitespace-pre-wrap">{result}</pre>}
        </div>
        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 bg-bg-card-soft/40 shrink-0">
          <button onClick={props.onDone} disabled={busy}
            className="px-4 py-2 text-[13px] text-text-dim hover:text-text border border-border rounded disabled:opacity-50">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button onClick={go} disabled={busy}
              className="px-4 py-2 text-[13px] bg-accent text-bg font-medium rounded hover:bg-accent-bright disabled:opacity-50">
              {busy ? 'Working…' : (props.kind === 'pdf' ? 'Upload + parse' : 'Import')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
